import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { billingApi, Invoice } from "../../api/billingApi";

interface VerificationResult {
  paymentStatus: string;
  amountTotal?: number;
  updatedInvoice: Invoice;
}

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();

  const invoiceId = searchParams.get("invoiceId");
  const sessionId = searchParams.get("session_id");
  const [invoice, setInvoice] = useState<Invoice | null>(null);

  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    if (hasVerifiedRef.current) {
        return;
    }

    hasVerifiedRef.current = true;

    const verifyPayment = async () => {
        if (!sessionId) {
        setLoading(false);
        return;
        }

        try {
        if (!invoiceId) {
            throw new Error("Invoice ID missing.");
        }

        const data = await billingApi.verifyStripeSession(
            sessionId,
            invoiceId
        ) as VerificationResult;

        setVerified(
            data.paymentStatus?.toLowerCase() === "paid"
        );

        setInvoice(data.updatedInvoice);

        } catch (error) {
        console.error(
            "Payment verification failed:",
            error
        );

        toast.error(
            "Unable to verify payment."
        );

        setVerified(false);
        } finally {
        setLoading(false);
        }
    };

    verifyPayment();
  }, [sessionId, invoiceId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 animate-spin text-strivre-burgundy" />
          <p className="text-slate-600">
            Verifying payment...
          </p>
        </div>
      </div>
    );
  }

  if (!verified) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="mx-auto max-w-2xl">
          <div className="rounded-3xl border border-red-200 bg-white p-10 text-center shadow-sm">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500" />

            <h1 className="mt-4 text-3xl font-semibold text-slate-900">
              Payment Verification Failed
            </h1>

            <p className="mt-3 text-slate-600">
              We were unable to verify the payment.
              Please contact support if you believe this is an error.
            </p>

            <Link
              to="/"
              className="mt-8 inline-flex rounded-full bg-white/10 px-5 py-3 text-white"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-2xl flex-col items-center">
        <div className="w-full rounded-3xl border border-emerald-200 bg-white p-8 shadow-sm sm:p-10">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-emerald-50 p-4 text-emerald-600">
              <CheckCircle className="h-12 w-12" />
            </div>
          </div>

          <div className="text-center">
            <h1 className="text-3xl font-semibold text-slate-900">
              Payment Successful
            </h1>

            <p className="mt-4 text-base text-slate-600">
              Thank you. Your payment has been verified and received successfully.
            </p>

            {invoice && (
                <div className="mt-6 rounded-2xl border border-strivre-lightnavy bg-slate-50 p-4">
                    <p className="text-sm font-medium text-slate-600">
                    Invoice Number
                    </p>

                    <p className="mt-1 text-base font-semibold text-slate-900">
                    {invoice.invoiceNumber}
                    </p>

                    <p className="mt-3 text-sm text-slate-500">
                    Amount Paid: ${invoice.paidAmount?.toFixed(2)}
                    </p>
                </div>
            )}

            {/* {sessionId && (
              <p className="mt-4 text-sm text-slate-500">
                Session ID: {sessionId}
              </p>
            )} */}

            {/* <div className="mt-8">
              <Link
                to="/"
                className="inline-flex items-center justify-center rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
              >
                Back to Home
              </Link>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}